import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { DevToolsService } from '@tytapp/app/dev';
import { environment } from '@tytapp/environment';
import { isClientSide, isOnline, isServerSide } from '@tytapp/environment-utils';
import { HostApi } from '@tytapp/host-api';
import { LocalStorageService } from 'angular-2-local-storage';
import { Angulartics2 } from 'angulartics2';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { BehaviorSubject } from 'rxjs';
import {
    ApiCommunicationPref, ApiConfiguration, ApiNewsletterOptins, ApiPermissionCheckResult, ApiPostUsersCurrentPermissionsCheck, ApiUser, ApiUserAuthToken, ApiUserSnippet,
    CommunicationPrefsApi, UsersApi
} from '../api';
import { AppConfig, Base64, Cookies, LoggerService, Redirection, RequestParams, Shell, sleep } from '../common';

import Bugsnag from '@bugsnag/js';
import jwtDecode from 'jwt-decode';

import * as uuid from 'uuid/v4';

const USER_KEY = makeStateKey<ApiUser>('tyt-user');

declare var dataLayer: any[];
declare var gtag: (...args) => void;

export type AudienceType = "member" | "expired member" | "registered" | "visitor";
export type AudienceFilter = (AudienceType | `-${AudienceType}` | 'all')[];

/**
 * Manages the current user and provides services for looking up and managing user accounts.
 *
 * NOTE:
 * WHY WE DO NOT USE LOCALSTORAGE FOR THE AUTH TOKEN:
 * It is far too easy for the cookie and the localStorage to disagree.
 * We should just rely on the cookie as the source of truth as it can
 * be read by both the client side and server side apps
 */
@Injectable()
export class UserService {
    private router = inject(Router);
    private apiConfig = inject(ApiConfiguration);
    private localStorage = inject(LocalStorageService);
    private shell = inject(Shell);
    private cookies = inject(Cookies);
    private requestParams = inject(RequestParams);
    private appConfig = inject(AppConfig);
    private usersApi = inject(UsersApi);
    private stateTransfer = inject(TransferState);
    private logger = inject(LoggerService);
    private angulartics2 = inject(Angulartics2);
    private angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
    private redirection = inject(Redirection);
    private hostApi = inject(HostApi);
    private base64 = inject(Base64);
    private platformId = inject(PLATFORM_ID);
    private devtools = inject(DevToolsService);
    private matSnackBar = inject(MatSnackBar);
    private commPrefsApi = inject(CommunicationPrefsApi);

    constructor() {
        this.readyPromise = new Promise<void>((res, rej) => this.markReady = res)
            .then(() => {
                this.isReady = true
            });

        this.devtools.rootMenu.items.push({
            id: 'beacons',
            type: 'menu',
            label: 'Beacons',
            icon: 'analytics',
            items: [
                {
                    id: 'beacons_status',
                    type: 'action',
                    label: this.devBeaconsStatus,
                    handler: (action, injector) => {
                        if (this.forceBeacons === true)
                            this.matSnackBar.open('Analytics beacons are currently: Force-Enabled.', undefined, { duration: 5000 });
                        else if (this.forceBeacons === false)
                            this.matSnackBar.open('Analytics beacons are currently: Force-Disabled.', undefined, { duration: 5000 });

                        this.matSnackBar.open(`Analytics beacons are currently: ${this.beaconsEnabled ? 'Enabled' : 'Disabled'}`, undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'enable_beacons',
                    type: 'action',
                    label: 'Force Enable',
                    handler: (action, injector) => {
                        localStorage['tyt:force_beacons'] = '1';
                        this.updateDevStatus();
                        this.matSnackBar.open('Analytics beacons are now force-enabled.', undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'disable_beacons',
                    type: 'action',
                    label: 'Force Disable',
                    handler: (action, injector) => {
                        localStorage['tyt:force_beacons'] = '0';
                        this.updateDevStatus();
                        this.matSnackBar.open('Analytics beacons are now force-disabled.', undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'reset_beacons',
                    type: 'action',
                    label: 'Unforce',
                    handler: (action, injector) => {
                        delete localStorage['tyt:force_beacons'];
                        this.updateDevStatus();
                        this.matSnackBar.open(`Analytics beacons are now set to default (${this.beaconsEnabled ? 'enabled' : 'disabled'})`, undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'show_beacons',
                    type: 'action',
                    label: 'Show Beacons',
                    handler: (action, injector) => {
                        localStorage['tyt:show_beacons'] = '1';
                        this.updateDevStatus();
                        this.matSnackBar.open(`Analytics beacons will now be shown in the snack bar`, undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'hide_beacons',
                    type: 'action',
                    label: 'Hide Beacons',
                    handler: (action, injector) => {
                        delete localStorage['tyt:show_beacons'];
                        this.updateDevStatus();
                        this.matSnackBar.open(`Analytics beacons will no longer show via the snack bar`, undefined, { duration: 5000 });
                    }
                },
                {
                    id: 'send_test_beacon',
                    type: 'action',
                    label: 'Send test beacon',
                    handler: (action, injector) => {
                        this.sendBeacons('This is a test', async () => {
                            this.sendBeaconGA4('engn_test', {
                                aTestParameter: 123
                            });

                            this.sendBeaconFB('engnTest', 'This is a test', {
                                aTestParameter: 123
                            });
                        });
                    }
                },
                {
                    id: 'send_error_beacon',
                    type: 'action',
                    label: 'Send beacon with error',
                    handler: (action, injector) => {
                        this.sendBeacons('This is a test', async () => {
                            this.sendBeaconGA4('engn_test', {
                                aTestParameter: 123
                            });

                            this.sendBeaconFB('engnTest', 'This is a test', {
                                aTestParameter: 123
                            });
                            throw new Error(`This is a fake error`);
                        });
                    }
                }
            ]
        });

        this.updateDevStatus();
    }

    private get devBeaconsStatus() {
        if (this.forceBeacons !== undefined)
            return `Status: Force-${this.forceBeacons ? 'Enabled' : 'Disabled'}`;
        else
            return `${this.beaconsEnabled ? 'Enabled' : 'Disabled'} by default`;
    }

    private updateDevStatus() {
        this.devtools.getActionById('enable_beacons').hidden = this.forceBeacons === true;
        this.devtools.getActionById('disable_beacons').hidden = this.forceBeacons === false;
        this.devtools.getActionById('reset_beacons').hidden = this.forceBeacons === undefined;
        this.devtools.getActionById('beacons_status').label = this.devBeaconsStatus;

        this.devtools.getActionById('show_beacons').hidden = this.showBeacons === true;
        this.devtools.getActionById('hide_beacons').hidden = this.showBeacons === false;
    }

    private _onboardingChanged = new BehaviorSubject<boolean>(false);
    private _onboarding = false;

    isReady = false;

    get onboardingChanged() {
        return this._onboardingChanged;
    }

    get onboarding() {
        return this._onboarding;
    }

    userToSnippet(user: ApiUser): ApiUserSnippet {
        return <ApiUserSnippet>{
            type: user.type,
            id: user.id,
            uuid: user.uuid,
            display_name: user.display_name,
            username: user.username,
            admin: user.admin,
            super_admin: user.super_admin,
            affiliated: user.affiliate ? true : false,
            staff: user.staff,
            avatar: user.profile ? user.profile.avatar : null
        };
    }

    async hasPermission(check: ApiPostUsersCurrentPermissionsCheck): Promise<boolean> {
        return (await this.checkPermission(check))?.allowed ?? false;
    }

    async checkPermission(check: ApiPostUsersCurrentPermissionsCheck): Promise<ApiPermissionCheckResult> {
        await this.ready;
        return await this.usersApi.checkPermission(check).toPromise();
    }

    async refresh() {
        if (isOnline())
            this.user = await this.usersApi.getCurrentUser().toPromise();
    }

    validateEmail(email: string) {
        if (!email)
            return 'Please enter the email address you would like to use.';

        if (email.endsWith('.con'))
            return 'The email you provided ends with ".con". You probably meant ".com"';

        if (email.endsWith('.comm'))
            return 'The email you provided ends with ".comm". You probably meant ".com"';

        if (email.endsWith('.ccom'))
            return 'The email you provided ends with ".ccom". You probably meant ".com"';

        if (email.includes('@gmail') && !email.endsWith('.com'))
            return 'Looks like you have a GMail address but you have specified the wrong domain name. Check the email address you\'ve entered for errors. ';

        return null;
    }

    set onboarding(value) {
        if (value === this._onboarding)
            return;

        this._onboarding = value;
        this._onboardingChanged.next(value);
        this.localStorage.set('onboarding', value);
    }

    private _user: ApiUser;

    public firstTime: boolean = true;

    userChanged: BehaviorSubject<ApiUser> = new BehaviorSubject<ApiUser>(null);
    guestChanged: BehaviorSubject<ApiUser> = new BehaviorSubject<ApiUser>(null);
    identityChanged: BehaviorSubject<ApiUser> = new BehaviorSubject<ApiUser>(null);

    sourceChanged: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    markReady: Function;
    readyPromise: Promise<void>;
    private _bounceUrl: string;

    get bounceURL() {
        if (isClientSide()) {
            let storedBounceUrl = localStorage['tyt.userService.bounceUrl'];
            if (storedBounceUrl)
                this._bounceUrl = storedBounceUrl;
        }
        return this._bounceUrl;
    }

    set bounceURL(value) {
        this._bounceUrl = value;
        if (isClientSide()) {
            if (value)
                localStorage['tyt.userService.bounceUrl'] = value;
            else
                delete localStorage['tyt.userService.bounceUrl'];
        }

        this.logger.info(`UserService: Return URL is ${value}`);
    }

    get identityKey() {
        if (this.user)
            return `user_${this.user.id}`;

        return `anonymous`;
    }

    get ready() {
        return this.readyPromise;
    }

    get token(): string {
        return <string>this.apiConfig.accessToken;
    }

    set token(value) {
        this.apiConfig.accessToken = value;
    }

    get sourceFirstTime(): boolean {
        return <boolean>this.localStorage.get('trafficSourceFirstTime');
    }

    private _sourceStamp: Date;

    get sourceStamp(): Date {
        if (!this._sourceStamp) {
            this._sourceStamp = new Date(this.localStorage.get('trafficSourceStamp'));
        }

        return this._sourceStamp;
    }

    decodeToken(token): any {
        return jwtDecode(token);
    }

    async refreshToken() {

        if (!this._user || !this.apiConfig.accessToken)
            return;

        let tokenStr = this.apiConfig.accessToken;
        let token = this.decodeToken(tokenStr);

        // Determine the appropriate renewal period before expiry

        let threshold = 60 * 60 * 8;

        if (token.renewalTime)
            threshold = token.renewalTime;
        else if (token.lifetime)
            threshold = Math.min(threshold, Math.round(token.lifetime * 0.25));

        let renewalTime = token.exp - threshold;

        if ((new Date().getTime() / 1000) < renewalTime)
            return;

        this.logger.info("Refreshing authentication...");
        let userAuthToken: ApiUserAuthToken;

        try {
            userAuthToken = await this.usersApi.refreshAuthentication(token.rememberMe || false).toPromise();
        } catch (e) {
            if (e.json)
                e = e.json();

            this.logger.error(`Failed to refresh auth token: ${e.error || e.message}`);
            this.logger.error(e);
            return;
        }

        token = this.decodeToken(userAuthToken.token);
        let timeMs = 1000 * (token.exp - token.renewalTime);

        this.logger.info(`Authentication refreshed. Renewal will happen at ${new Date(timeMs)}`);
        this.saveAuthentication(userAuthToken, userAuthToken.token);
    }

    async refreshTokenPeriodically() {

        await this.refreshToken();

        // Do not wait to refresh token for server-side rendering.
        // (Infinite loads)

        if (!isPlatformServer(this.platformId)) {
            setTimeout(() => {
                this.refreshTokenPeriodically();
            }, this._tokenRefreshInterval);
        }
    }

    private _tokenRefreshInterval: number = 1000 * 60 * 30;
    private _postLoginUrl: string = null;
    private _postLoginAction: Function = null;
    private _source: string = null;

    set postLoginUrl(value: string) {
        if (value != null)
            value = value.replace(/^\/+/, '');

        this._postLoginUrl = value;
        this._postLoginAction = null;
    }

    get postLoginUrl() {
        return this._postLoginUrl;
    }

    requireLogin() {
        if (!this.user) {
            this.router.navigate(['login']);
            return false;
        }

        return true;
    }

    get decodedToken() {
        return jwtDecode<{ type: 'Guest' | 'User', sub: string }>(this.token);
    }

    /**
     * Do not navigate after login is complete. Used for inline signups.
     */
    stayAfterLogin() {
        this.afterLogin(() => { });
    }

    /**
     * Run the given function after login (and do not otherwise navigate)
     * @param action
     */
    afterLogin(action: Function) {
        this._postLoginAction = action;
        this._postLoginUrl = null;
    }

    private _initialized = false;

    private async authenticateAsGuest() {
        let guest = await this.usersApi.authenticate({
            method: 'guest'
        }).toPromise();

        this.saveAuthentication(guest, guest.token);
    }

    async init() {
        if (this._initialized)
            return;
        this._initialized = true;

        if (isClientSide()) {

            window.addEventListener('online', async () => {
                if (!this.token) {
                    await this.authenticateAsGuest();
                }
            });

            try {
                if (typeof BroadcastChannel !== 'undefined') {
                    this.broadcastChannel = new BroadcastChannel('user-state');
                    this.broadcastChannel.addEventListener('message', ev => {
                        if (ev.data.type === 'user-changed') {
                            this.token = ev.data.token;
                            this.hostApi.sendMessage({ type: 'auth_token_changed', token });
                            this.locallyUpdateUser(ev.data.user, false);
                        }
                    })
                }
            } catch (e) {
                this.logger.error(`[UserService] Failed to set up BroadcastChannel! Error:`);
                this.logger.error(e);
            }
        }

        if (this.localStorage.get('onboarding')) {
            this.onboarding = true;
        }

        let repeatVisitorCookie = this.cookies.get('repeatVisitor');
        if (repeatVisitorCookie) {
            this.firstTime = false;
        } else {
            this.firstTime = true;
            this.localStorage.set('repeatVisitor', "1");
            this.cookies.set('repeatVisitor', 1, 1000 * 60 * 60 * 24 * 365 * 5);
        }

        // TODO: swap out with equivalent Location service (this.location)
        let token: string = null;

        if (typeof location !== 'undefined') {
            let args = this.requestParams.params;
            if (args.token) {
                this.logger.log(`Storing passed token ${args.token}`);
                this.cookies.set(environment.auth.cookie, { token: args.token });

                // Remove the token from the current URL to avoid
                // leaking tokens if the URL is shared

                let newUrl = location.href.replace(/token=[^&]+/, 'tx=');
                history.replaceState({}, document.title, newUrl);
            }

            if (args._usid) {
                let newSource = args._usid;
                let currentSource = this.source;
                if (newSource != currentSource) {
                    if (this.source)
                        this.logger.info(`Traffic source changed from ${currentSource} to ${newSource}`);
                    else
                        this.logger.info(`Traffic source: ${newSource}`);

                    this.source = newSource;
                }
            }
        }

        let authCookie = this.cookies.get<any>(environment.auth.cookie);
        let authCookieEnabled = true;

        if (isClientSide() && window['disableCookieAuthentication'])
            authCookieEnabled = false;

        if (authCookieEnabled && authCookie && !token)
            token = authCookie.token;

        if (!token && isOnline()) {
            await this.authenticateAsGuest();
        }

        this.token = token;
        this.hostApi.sendMessage({ type: 'auth_token_changed', token });

        if (authCookieEnabled && this.apiConfig.accessToken)
            this.cookies.set(environment.auth.cookie, { token: this.apiConfig.accessToken });

        let user: ApiUser;

        if (token) {
            let jwt = jwtDecode<{ type: 'Guest' | 'User', sub: string }>(token);

            this.logger.info(`Signed in as ${jwt.type} ${jwt.sub}`);

            user ??= this.localStorage.get('user');

            if (user)
                this.user = user;

            // Fetch/refresh the User object unless it was provided via SSR.
            if (isOnline()) {
                try {
                    user = await this.usersApi.getCurrentUser().toPromise();
                } catch(e) {
                    if (e.json)
                        e = e.json();

                    this.logger.error(`Failed to fetch user, auth token is likely expired. (${token})`);
                    this.logger.error(`Error Details: ${e.stack ?? e}`);

                    token = null;
                    this.router.navigate(['login']);
                }
            } else {
                this.logger.info(`[UserService] Skipping user refresh: Client is offline.`);
            }
        }

        if (user?.disabled) {
            token = null;
            user = null;
        }

        this.saveAuthentication(user, token);
        this.markReady();
        this.refreshTokenPeriodically();
        this.fetchAvailableAccounts();

    }

    resetSource() {
        this.localStorage.remove('trafficSource');
        this.localStorage.remove('trafficSourceFirstTime');
        this.source = null;
    }

    saveAuthentication(user: ApiUser, token: string) {
        let jwt: { type: 'User' | 'Guest', sub: string };

        if (token)
            jwt = jwtDecode<{ type: 'User' | 'Guest', sub: string }>(token);

        if (!token) {
            this.token = null;
            this.hostApi.sendMessage({ type: 'auth_token_changed', token: null });
            this.localStorage.remove('user');
            this.cookies.remove(environment.auth.cookie);
            this.cookies.remove(environment.auth.userCookie);
            return;
        }

        if (token && environment.showDevTools) {
            this.logger.info(`Saved auth for ${jwt.type} ${jwt.sub}`);
        }

        let authCookieEnabled = true;
        if (isClientSide() && window['disableCookieAuthentication'])
            authCookieEnabled = false;

        this.token = token;
        this.hostApi.sendMessage({ type: 'auth_token_changed', token });
        this.localStorage.set('user', user);
        if (authCookieEnabled && this.apiConfig.accessToken) {
            this.cookies.set(environment.auth.cookie, { token });
            this.cookies.set(environment.auth.userCookie, jwt.sub, null, true);
        }
        this.user = user;
    }

    completeLogin(user: ApiUser, token: string, hideDialogs = true, defaultAction = null) {
        this.saveAuthentication(user, token);
        this.addAvailableAccount({
            avatarUrl: user.profile?.avatar,
            displayName: user.display_name,
            email: user.email,
            id: user.id,
            username: user.username,
            uuid: user.uuid,
            staff: user.staff,
            token
        });

        if (hideDialogs)
            this.shell.hideDialog();

        setTimeout(() => {
            if (this._postLoginAction) {
                this._postLoginAction();
            } else if (this.postLoginUrl) {
                let destination = this.postLoginUrl;
                this.postLoginUrl = null;
                this.router.navigateByUrl(destination);
            } else if (defaultAction) {
                defaultAction();
            } else {
                this.redirection.go('/login/return');
                // this.router.navigate(['login/return']);
            }
        }, 100);
    }

    async completeLogout(removeAvailableAccounts = true) {
        if (removeAvailableAccounts)
            this.removeAllAvailableAccounts();

        let guestToken: string;

        if (isOnline()) {
            try {
                this.authenticateAsGuest();
            } catch (e) {
                this.logger.error(`Failed to obtain a guest token during logout!`);
                this.logger.error(e);
                this.saveAuthentication(null, null);
            }
        } else {
            this.saveAuthentication(null, null);
        }

    }

    async assume(token) {
        if (!token)
            throw new Error("Cannot assume a user without an access token.");

        this.logger.info(`Assuming user with token ${token}`);

        let originalToken = null;

        if (this.token)
            originalToken = this.token;

        // Store the previous authentication details

        this.token = token;
        this.hostApi.sendMessage({ type: 'auth_token_changed', token });

        let user: ApiUser;
        this.logger.info(`Fetching the assumed user from API...`);
        user = <any>await this.usersApi.getCurrentUser().toPromise();

        this.logger.info(`User assumption: Success: Now logged in as ${user.email} (${user.uuid})`);

        user['assumed'] = true;
        this.completeLogin(user, token, false, () => { });
        this.localStorage.set('originalToken', originalToken);
    }

    get source() {
        if (!this._source) {
            if (this.localStorage.get('trafficSource')) {
                this._source = <string>this.localStorage.get('trafficSource');
            }
        }

        return this._source;
    }

    get recentSource() {
        let time = this.sourceStamp || new Date();
        if (time.getTime() + 1000 * 60 * 60 * 3 < Date.now()) // is expired?
            return null;

        return this.source;
    }

    set source(value: string) {
        this.logger.info(`Setting attribution source to '${value}'`);
        this._source = value;
        this._sourceStamp = new Date();

        this.localStorage.set('trafficSource', value);
        this.localStorage.set('trafficSourceFirstTime', this.firstTime);
        this.localStorage.set('trafficSourceStamp', this._sourceStamp.toISOString());

        this.sourceChanged.next(value);
    }

    get loggedIn() {
        return this.user != null && this.apiConfig.accessToken != null;
    }

    userUpdatedAt: number;

    set user(user: ApiUser) {
        this.userUpdatedAt = Date.now();
        this._user = user;

        this.locallyUpdateUser(user);
        if (isPlatformServer(this.platformId))
            this.stateTransfer.set<ApiUser>(USER_KEY, user);
    }

    goToLogin(returnURL?: string) {
        if (!returnURL)
            returnURL = isClientSide() ? location.href : '/';
        if (returnURL.startsWith('/'))
            returnURL = `${environment.urls.root}${returnURL}`;

        this.redirection.go(`/login?${returnURL ? `return=${encodeURIComponent(returnURL)}` : ``}`);
    }

    /**
     * Apply a new user object to the entire application.
     * This does not change login state, you must only
     * pass a modified version of the LOGGED IN USER here
     * or else bad things will happen!
     */
    locallyUpdateUser(user: ApiUser, sendBroadcast = true) {
        this._user = user;

        // Log out the change

        if (isClientSide()) {
            if (user?.type === 'User') {
                this.logger.info(`User ${user.email} [${user.uuid}] is signed in.`);
            } else if (user?.type === 'Guest') {
                this.logger.info(`Guest ${user.uuid} is signed in.`);
            } else if (user) {
                this.logger.info(`Identity [${user.type}] ${user.uuid} is signed in.`);
            } else {
                this.logger.info(`User is now signed out.`);
            }
        }

        // Let Bugsnag know about the user change

        if (isClientSide()) {
            if (user) {
                Bugsnag.setUser(
                    user.uuid,
                    user.email,
                    user.display_name
                );
            } else {
                Bugsnag.setUser(
                    'logged-out',
                    'logged-out@tyt.com',
                    'Logged Out'
                );
            }
        } else {
            let requestContext = Zone.current.get('requestContext');
            if (requestContext) {
                requestContext.user = {
                    id: this._user?.uuid,
                    email: this._user?.email,
                    name: this._user.type === 'Guest' ? `Guest` : this._user?.username
                }
            }
        }

        // Notify all subscriptions

        this.identityChanged.next(this._user);

        if (this._user) {
            if (this._user.type === 'Guest') {
                this.guestChanged.next(this._user);
                this.userChanged.next(null);
            } else {
                this.userChanged.next(this._user);
                this.guestChanged.next(null);
            }
        } else {
            this.userChanged.next(null);
            this.guestChanged.next(null);
        }

        // Add user information to the current request if we are in SSR (used in Bugsnag error reporting)

        // Notify other tabs

        if (sendBroadcast && isClientSide() && this.broadcastChannel) {
            this.broadcastChannel.postMessage({ type: 'user-changed', user: this._user, token: this.token });
        }

    }

    broadcastChannel: BroadcastChannel;

    get isLoggedIn() {
        return this._user != null;
    }

    get identity() {
        return this._user;
    }

    get user() {
        if (this._user?.type === 'User')
            return this._user;
        else
            return null;
    }

    get guest() {
        if (this._user?.type === 'User')
            return this._user;
        else
            return null;
    }

    /**
     * True if the current user identity is entitled (either User or Guest).
     */
    get entitled() {
        return this._user?.entitled;
    }


    matchesAudience(audiences: AudienceFilter) {
        if (!audiences)
            return true;

        if (audiences.includes('all')) {
            return !audiences.includes(`-${this.audienceType}`);
        } else {
            return audiences.includes(this.audienceType);
        }
    }

    get audienceType(): AudienceType {
        // Note that _user is different than user here (_user will be set even if the identity is a Guest)

        if (this._user?.entitled)
            return 'member';

        if (this._user?.membership)
            return 'expired member';

        if (this.user)
            return 'registered';

        return 'visitor';
    }

    get forceBeacons() {
        if (!isClientSide())
            return undefined;

        if (localStorage['tyt:force_beacons'] === undefined)
            return undefined;

        return environment.showDevTools && localStorage['tyt:force_beacons'] === '1';
    }

    get showBeacons() {
        if (!isClientSide())
            return false;

        return environment.showDevTools && localStorage['tyt:show_beacons'] === '1';
    }

    get beaconsEnabled() {
        return environment.name === 'production' || this.forceBeacons;
    }

    /**
     * Send event beacons to Google Analytics and Facebook Pixel because a
     * new user registration has just occurred (and said user is now logged in)
     */
    async sendRegisteredBeacons() {
        await this.sendBeacons('Registered via Email/Password', () => {
            // Standard GA custom event
            this.sendBeaconGA4('sign_up', {
                method: 'email_and_password',
                app: 'tyt.com',
                path: 'free_signup'
            });
            this.sendBeaconFB('CompleteRegistration', 'tytcom_registered', {
                registration_type: 'tyt_account'
            });
        });
    }

    async sendSocialSignupAnalytics(method: 'free-signup' | 'login') {
        await this.sendBeacons(`Social Signup [${method}]`, () => {
            this.sendBeaconGA4('sign_up', {
                method: 'social',
                app: 'tyt.com',
                path: (method ?? 'unknown').replace(/-/g, '_')
            });
            this.sendBeaconFB('CompleteRegistration', `tytcom_${method}_social_registered`, {
                registration_type: 'tyt_account',
                social_method: method
            });
        });
    }

    async sendBeacons(label: string, callback: Function) {
        if (isServerSide()) {
            this.logger.error(`Fatal Error: App requested beacons be sent for '${label}' on the server side! Ensure that beacons are only sent client side! These beacons will be skipped!`);
            return;
        }

        const beaconMessage = `[Beacon${!this.beaconsEnabled ? '/simulated' : ''}] ${label}`;
        this.logger.info(beaconMessage);

        try {
            const beacon = { label, tags: [] };
            await Zone.current
                .fork({
                    name: 'Analytics beacons',
                    properties: {
                        'tyt:beacon': beacon
                    }
                })
                .run(() => callback())
            ;

            if (this.showBeacons) {
                this.matSnackBar.open(`${beaconMessage} | Events: ${beacon.tags.join(', ')}`, undefined, { duration: 5000 });
            }

        } catch (e) {
            this.logger.error(`Caught error while trying to send beacons for event: ${label}`);
            this.logger.error(e);
            this.logger.error(`These beacons will not be retried.`);

            if (this.showBeacons) {
                this.matSnackBar.open(`[ERROR] ${beaconMessage} | Caught error while sending: ${e.message}`, undefined, { duration: 5000 });
            }
        }

        // Make sure enough time is allowed for the beacons to go out.

        await sleep(3000);
        if (isClientSide() && localStorage['tyt:beacon-logging'] !== 'quiet')
            this.logger.info(`Beacon sending completed.`);
    }

    get currentBeacon() {
        return Zone.current.get('tyt:beacon') as { label: string, tags: string[] };
    }

    sendBeaconFB(eventName: string, contentName: string, metadataInit: Record<string, any> = {}, isCustom = false) {
        if (isServerSide())
            return;

        const eventId = `${eventName}_${uuid()}`;
        const metadata = {
            content_name: contentName,
            user_uuid: this.user ? this.user.uuid : null,
            ...metadataInit,
            eventID: eventId,
        }

        this.currentBeacon.tags.push(`FBA:${eventName}`);

        console.groupCollapsed(`[FBA] ${eventName}/${contentName}, ID: ${eventId} ${!this.beaconsEnabled? '[skipped]' : ''}`);
        console.dir(metadata);
        console.groupEnd();

        if (!this.beaconsEnabled)
            return;

        if (this.hostApi.apiEnabled) {
            this.hostApi.sendMessage({
                type: 'fb_event',
                name: eventName,
                params: metadata
            });
        } else {
            let xfbq = window['xfbq'];
            if (xfbq === undefined)
                return;

            if (this.beaconsEnabled) {
                xfbq(
                    isCustom ? 'trackCustom' : 'track',
                    eventName,
                    metadata
                );
            }
        }
    }

    sendBeaconGA4(name: string, params: Record<string, any> = {}) {
        if (isServerSide())
            return;

        // Notify GA4 directly of the event, because GTM would require us to set up a trigger and tag for every
        // individual event in order to send custom parameters.

        this.currentBeacon.tags.push(`GA4:${name}`);
        console.groupCollapsed(`[GA4] ${name} ${!this.beaconsEnabled? '[skipped]' : ''}`);
        console.dir(params);
        console.groupEnd();

        if (!this.beaconsEnabled)
            return;

        if (this.hostApi.apiEnabled) {
            this.hostApi.sendMessage({
                type: 'ga_event',
                name: name,
                params
            });
        } else {
            // It is IMPORTANT to call gtag() here and NOT do dataLayer.push([ ... ])
            // gtag.js detects the Arguments object to determine if a datalayer push is a gtag command!
            // This is undocumented and is a horrible design! But that's what you get when you let Google try to
            // provide a simple service!
            gtag('event', name, params);
        }
    }

    async sendSearchAnalytics(event: 'search' | 'view_search_results', search_term: string) {
        if (!isClientSide())
            return;

        await this.sendBeacons(`${ event } event with terms '${ search_term }'`, () => {
            this.sendBeaconGA4(event, { search_term });
        });
    }

    private sendAppWebEvent(event: string, props: Record<string, any>) {
        this.angulartics2GoogleTagManager.eventTrack(
            event,
            Object.assign({}, props, { event })
        );
    }

    private _availableAccountsChanged = new BehaviorSubject<AvailableAccount[]>([]);
    private _availableAccountsChanged$ = this._availableAccountsChanged.asObservable();
    get availableAccountsChanged() { return this._availableAccountsChanged$; }

    private _availableAccounts: AvailableAccount[] = [];
    get availableAccounts() { return this._availableAccounts.slice(); }

    get switchAccountsKey() {
        return `tyt:accounts:${environment.apiOverride ?? 'default'}`;
    }

    private fetchAvailableAccounts() {
        if (isServerSide() || !localStorage[this.switchAccountsKey]) {
            this._availableAccountsChanged.next([]);
            this._availableAccounts = [];
            return [];
        }

        try {
            let accounts = JSON.parse(this.base64.decode(localStorage[this.switchAccountsKey]));
            this._availableAccountsChanged.next(accounts);
            this._availableAccounts = accounts;
            return accounts;
        } catch (e) {
            this.logger.error(`Failed to parse localStorage[${this.switchAccountsKey}]: ${e.message}`);
            this.logger.error(`Contents was: '${localStorage[this.switchAccountsKey]}'`);
            delete localStorage[this.switchAccountsKey];
            return [];
        }
    }

    addAvailableAccount(account: AvailableAccount) {
        if (isServerSide())
            return;
        let accounts = this.fetchAvailableAccounts();

        accounts = accounts.filter(x => x.id !== account.id);
        accounts.unshift(account);

        localStorage[this.switchAccountsKey] = this.base64.encode(JSON.stringify(accounts));
        this.fetchAvailableAccounts();
    }

    removeAvailableAccount(account: AvailableAccount) {
        if (isServerSide())
            return;
        let accounts = this.fetchAvailableAccounts();
        accounts = accounts.filter(x => x.id !== account.id);
        localStorage[this.switchAccountsKey] = this.base64.encode(JSON.stringify(accounts));
        this.fetchAvailableAccounts();
    }

    removeAllAvailableAccounts() {
        if (isServerSide())
            return;
        delete localStorage[this.switchAccountsKey];
        this.fetchAvailableAccounts();
    }

    isCurrentlySignedIntoAvailableAccount(account: AvailableAccount) {
        if (this.user?.id === account.id)
            return true;
        return false;
    }

    async switchToAvailableAccount(account: AvailableAccount) {
        this.token = account.token;
        this.hostApi.sendMessage({ type: 'auth_token_changed', token: account.token });

        this.logger.info(`Fetching the assumed user from API...`);
        let user = await this.usersApi.getCurrentUser().toPromise();

        this.logger.info(`[Account Switcher]: Success: Now logged in as ${user.email} (${user.uuid})`);

        this.completeLogin(user, account.token, false, () => { });
    }

    async saveCommPrefs(optins: ApiNewsletterOptins, provider = 'email') {
        try {
            const patch: ApiCommunicationPref[] = Object.entries(optins).map(
                ([provider_id, allowed]) => ({
                    provider_id,
                    provider,
                    allowed
                })
            );
            await this.commPrefsApi.patchCommunicationPrefs({
                communication_prefs: patch
            }).toPromise();
        } catch (e) {
            this.logger.error('Failed to save communication preferences: ');
            this.logger.error(e);
        }
    }

    getPublicProfile(username: string) {
        return this.usersApi.getPublicProfile(username).toPromise();
    }
}

export interface AvailableAccount {
    id: number;
    uuid: string;
    displayName: string;
    username: string;
    email: string;
    avatarUrl: string;
    token: string;
    staff: boolean;
}
